import { Locale } from '../types/global';

export const API_BASE_URL = import.meta.env.VITE_PAYLOAD_API_URL ?? 'http://localhost:3000';
const POSTS_ENDPOINT = `${API_BASE_URL}/api/posts`;

const APP_LOCALES: Locale[] = ['en', 'zh'];
const APP_TO_PAYLOAD_LOCALE: Record<Locale, string> = {
  en: 'en',
  zh: 'zh-CN'
};

type PayloadLexicalNode = {
  type: string;
  text?: string;
  children?: PayloadLexicalNode[];
  [key: string]: unknown;
};

export type PayloadRichText = {
  root?: PayloadLexicalNode;
  [key: string]: unknown;
};

type PayloadPost = {
  id: number;
  title?: string | null;
  content?: PayloadRichText | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

type PostListResponse = {
  docs: PayloadPost[];
  totalDocs?: number;
  totalPages?: number;
  nextPage?: number | null;
  hasNextPage?: boolean;
};

export type Post = {
  id: string;
  title: Record<Locale, string>;
  content: Record<Locale, PayloadRichText | null>;
  createdAt: string;
};

const fetchPostsForLocale = async (locale: Locale): Promise<PayloadPost[]> => {
  const payloadLocale = APP_TO_PAYLOAD_LOCALE[locale];
  const params = new URLSearchParams({
    limit: '100',
    locale: payloadLocale,
    depth: '1',
    sort: '-createdAt'
  });

  const response = await fetch(`${POSTS_ENDPOINT}?${params.toString()}`);
  if (!response.ok) {
    throw new Error(`Failed to load posts (${locale}): ${response.status} ${response.statusText}`);
  }

  const data = (await response.json()) as PostListResponse;
  return data.docs;
};

export const loadPosts = async (): Promise<Post[]> => {
  const localeResults = await Promise.all(
    APP_LOCALES.map(async (locale) => ({
      locale,
      docs: await fetchPostsForLocale(locale)
    }))
  );

  const englishDocs = localeResults.find((r) => r.locale === 'en')?.docs ?? [];

  const byId = new Map<number, Post>();

  englishDocs.forEach((doc) => {
    byId.set(doc.id, {
      id: String(doc.id),
      title: { en: doc.title ?? '', zh: '' },
      content: { en: doc.content ?? null, zh: null },
      createdAt: doc.createdAt ?? new Date().toISOString()
    });
  });

  localeResults
    .filter((r) => r.locale !== 'en')
    .forEach(({ locale, docs }) => {
      docs.forEach((doc) => {
        const entry = byId.get(doc.id);
        if (!entry) return;
        entry.title[locale] = doc.title ?? entry.title.en;
        entry.content[locale] = doc.content ?? entry.content.en;
      });
    });

  return Array.from(byId.values());
};
